import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import TopNav from "@/components/TopNav";
import api, { inr, formatApiErrorDetail } from "@/lib/api";

const I18N_LANGS = [
  ["hi", "हिन्दी · Hindi"], ["mr", "मराठी · Marathi"], ["bn", "বাংলা · Bengali"],
  ["te", "తెలుగు · Telugu"], ["ta", "தமிழ் · Tamil"], ["gu", "ગુજરાતી · Gujarati"],
  ["kn", "ಕನ್ನಡ · Kannada"], ["es", "Español · Spanish"], ["fr", "Français · French"],
  ["pt", "Português · Portuguese"], ["de", "Deutsch · German"], ["id", "Bahasa Indonesia · Indonesian"],
];

export default function Admin() {
  const [users, setUsers] = useState([]);
  const [settings, setSettings] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [usage, setUsage] = useState([]);
  const [adjust, setAdjust] = useState(null); // {user_id, name}
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [savingSettings, setSavingSettings] = useState(false);
  const [i18nLang, setI18nLang] = useState("hi");
  const [i18nStrings, setI18nStrings] = useState({});
  const [dub, setDub] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const [u, s, r, g, d] = await Promise.all([
        api.get("/admin/users"),
        api.get("/admin/settings"),
        api.get("/admin/revenue"),
        api.get("/admin/usage"),
        api.get("/admin/dubbing").catch(() => null),
      ]);
      setUsers(u.data.users || []);
      setSettings(s.data);
      setRevenue(r.data);
      setUsage(g.data.usage || []);
      setDub(d?.data || null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const loadI18n = useCallback(async (lang) => {
    setI18nLang(lang);
    try {
      const { data } = await api.get(`/admin/i18n/${lang}`);
      setI18nStrings(data.strings || {});
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  }, []);

  useEffect(() => {
    loadI18n("hi");
  }, [loadI18n]);

  const saveI18n = async () => {
    try {
      await api.put(`/admin/i18n/${i18nLang}`, { strings: i18nStrings });
      toast.success("Translations saved. Pages pick the wording up right away.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const payload = {
        ...settings,
        packs: String(settings.packs).split(",").map((x) => parseInt(String(x).trim(), 10)).filter((n) => !isNaN(n)),
      };
      const { data } = await api.put("/admin/settings", payload);
      setSettings(data);
      toast.success("Settings saved.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSavingSettings(false);
    }
  };

  const doAdjust = async () => {
    try {
      await api.post(`/admin/users/${adjust.user_id}/wallet`, { amount_rupees: parseFloat(amount), reason });
      toast.success("Wallet adjusted.");
      setAdjust(null);
      setAmount("");
      setReason("");
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const activatePlan = async (userId, planType) => {
    try {
      await api.post(`/admin/users/${userId}/activate-plan`, { plan_type: planType });
      toast.success(`Plan activated (${planType}). Note: manual activation does not add wallet credit.`);
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const toggleBlock = async (userId) => {
    try {
      await api.post(`/admin/users/${userId}/block`);
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const set = (k, v) => setSettings((s) => ({ ...s, [k]: v }));

  const input = (k, label, type = "text", testid) => (
    <div>
      <label className="mb-1.5 block font-mono text-xs uppercase tracking-[0.15em] text-muted">{label}</label>
      <input
        type={type}
        value={settings[k] ?? ""}
        onChange={(e) => set(k, type === "number" ? parseFloat(e.target.value) : e.target.value)}
        data-testid={testid || `admin-setting-${k}`}
        className="w-full rounded-xl border border-line bg-surface px-3 py-2 text-sm outline-none focus:border-accent"
      />
    </div>
  );

  const i18nKeys = Object.keys(i18nStrings).sort((a, b) => a.localeCompare(b));

  return (
    <div className="min-h-screen bg-bg" data-testid="admin-page">
      <TopNav active="admin" />
      <main className="mx-auto max-w-6xl px-5 py-12">
        <h1 className="font-display text-4xl font-extrabold tracking-tight">Admin</h1>

        {/* Users */}
        <section className="mt-8" data-testid="admin-users-section">
          <h2 className="font-display text-2xl font-extrabold">Users ({users.length})</h2>
          <div className="mt-4 overflow-x-auto rounded-2xl border border-line bg-surface">
            <table className="w-full text-left text-sm" data-testid="admin-users-table">
              <thead>
                <tr className="border-b border-line font-mono text-xs uppercase tracking-wider text-muted">
                  <th className="px-4 py-3">User</th>
                  <th className="px-4 py-3">Plan</th>
                  <th className="px-4 py-3 text-right">Wallet</th>
                  <th className="px-4 py-3">Last active</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.user_id} className="border-b border-line/60 last:border-0">
                    <td className="px-4 py-3">
                      <p className="font-bold">{u.name}</p>
                      <p className="text-xs text-muted">{u.email}{u.role === "admin" ? " · admin" : ""}</p>
                    </td>
                    <td className="px-4 py-3">
                      {u.plan?.active ? (
                        <span className="text-good">{u.plan.type} · until {(u.plan.expires_at || "").slice(0, 10)}</span>
                      ) : (
                        <span className="text-warn">No plan</span>
                      )}
                      {u.email_verified === false && <p className="text-xs text-warn">email unverified</p>}
                      {u.blocked && <p className="text-xs text-bad">blocked</p>}
                    </td>
                    <td className="px-4 py-3 text-right font-mono">{inr(u.wallet_balance || 0)}</td>
                    <td className="px-4 py-3 font-mono text-xs text-muted">{(u.last_active || "").slice(0, 10) || "—"}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap justify-end gap-2">
                        <button onClick={() => { setAdjust(u); setAmount(""); setReason(""); }} data-testid={`admin-wallet-${u.email}`}
                          className="rounded-full border border-line px-3 py-1 text-xs font-bold hover:border-accent hover:text-accent">Wallet</button>
                        {u.role !== "admin" && (
                          <>
                            <button onClick={() => activatePlan(u.user_id, "monthly")} data-testid={`admin-activate-monthly-${u.email}`}
                              className="rounded-full border border-line px-3 py-1 text-xs font-bold hover:border-accent hover:text-accent">Activate monthly</button>
                            <button onClick={() => activatePlan(u.user_id, "yearly")} data-testid={`admin-activate-yearly-${u.email}`}
                              className="rounded-full border border-line px-3 py-1 text-xs font-bold hover:border-accent hover:text-accent">Activate yearly</button>
                            <button onClick={() => toggleBlock(u.user_id)} data-testid={`admin-block-${u.email}`}
                              className={`rounded-full border px-3 py-1 text-xs font-bold ${u.blocked ? "border-good text-good" : "border-bad text-bad"}`}>
                              {u.blocked ? "Unblock" : "Block"}
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Settings + Revenue */}
        <div className="mt-14 grid gap-6 lg:grid-cols-2">
          <section className="rounded-3xl border border-line bg-surface p-7 shadow-card" data-testid="admin-settings-section">
            <h2 className="font-display text-2xl font-extrabold">Settings</h2>
            {settings && (
              <>
                <div className="mt-5 grid gap-4 sm:grid-cols-2">
                  {input("monthly_price", "Monthly price (₹, excl GST)", "number", "admin-setting-monthly")}
                  {input("yearly_price", "Yearly price (₹, excl GST)", "number", "admin-setting-yearly")}
                  {input("gst_rate", "GST rate (0.18)", "number", "admin-setting-gst")}
                  {input("markup", "Paid-action markup (0.20)", "number", "admin-setting-markup")}
                  {input("credit_expiry_months", "Credit expiry (months)", "number", "admin-setting-expiry")}
                  {input("included_credit", "First-payment credit (₹)", "number", "admin-setting-credit")}
                  {input("grace_days", "Grace days after lapse", "number", "admin-setting-grace")}
                  {input("rate_limit_per_min", "AI rate limit / min", "number", "admin-setting-ratelimit")}
                  {input("business_name", "Business name")}
                  {input("gstin", "GSTIN")}
                  {input("sac_code", "SAC code")}
                  {input("business_state", "Business state")}
                  {input("contact_email", "Contact email")}
                </div>
                <div className="mt-4">
                  <label className="mb-1.5 block font-mono text-xs uppercase tracking-[0.15em] text-muted">Address</label>
                  <textarea value={settings.address ?? ""} onChange={(e) => set("address", e.target.value)} data-testid="admin-setting-address" rows={2}
                    className="w-full rounded-xl border border-line bg-surface px-3 py-2 text-sm outline-none focus:border-accent" />
                </div>
                <div className="mt-4">
                  <label className="mb-1.5 block font-mono text-xs uppercase tracking-[0.15em] text-muted">Credit packs (comma separated ₹)</label>
                  <input value={(settings.packs || []).join(", ")} onChange={(e) => set("packs", e.target.value)} data-testid="admin-setting-packs"
                    className="w-full rounded-xl border border-line bg-surface px-3 py-2 text-sm outline-none focus:border-accent" />
                </div>
                <button onClick={saveSettings} disabled={savingSettings} data-testid="admin-save-settings"
                  className="mt-6 rounded-full bg-accent px-7 py-2.5 text-sm font-bold text-white transition-transform hover:-translate-y-0.5 disabled:opacity-50">
                  {savingSettings ? "Saving…" : "Save settings"}
                </button>
              </>
            )}
          </section>

          <section className="rounded-3xl border border-line bg-surface p-7 shadow-card" data-testid="admin-revenue-section">
            <h2 className="font-display text-2xl font-extrabold">Revenue</h2>
            {revenue && (
              <>
                <div className="mt-4 grid grid-cols-3 gap-3 text-center">
                  <div className="rounded-2xl bg-bg p-4">
                    <p className="font-mono text-xs text-muted">Invoices</p>
                    <p className="mt-1 font-display text-2xl font-extrabold" data-testid="revenue-count">{revenue.count}</p>
                  </div>
                  <div className="rounded-2xl bg-bg p-4">
                    <p className="font-mono text-xs text-muted">Taxable</p>
                    <p className="mt-1 font-display text-2xl font-extrabold" data-testid="revenue-taxable">{inr(revenue.total_taxable_paise || 0)}</p>
                  </div>
                  <div className="rounded-2xl bg-bg p-4">
                    <p className="font-mono text-xs text-muted">GST</p>
                    <p className="mt-1 font-display text-2xl font-extrabold" data-testid="revenue-gst">{inr(revenue.total_gst_paise || 0)}</p>
                  </div>
                </div>
                <p className="mt-6 font-mono text-xs uppercase tracking-[0.2em] text-muted">By day</p>
                <div className="mt-2 max-h-48 overflow-y-auto rounded-xl border border-line" data-testid="revenue-by-day">
                  <table className="w-full text-left text-xs font-mono">
                    {revenue.days?.length === 0 && <tr><td className="px-3 py-3 text-muted">No payments yet.</td></tr>}
                    {revenue.days?.map((d) => (
                      <tr key={d.date} className="border-b border-line/50 last:border-0">
                        <td className="px-3 py-2 text-muted">{d.date}</td>
                        <td className="px-3 py-2 text-right">{inr(d.total_paise)}</td>
                      </tr>
                    ))}
                  </table>
                </div>
                <p className="mt-4 font-mono text-xs uppercase tracking-[0.2em] text-muted">By month</p>
                <div className="mt-2 max-h-40 overflow-y-auto rounded-xl border border-line" data-testid="revenue-by-month">
                  <table className="w-full text-left text-xs font-mono">
                    {revenue.months?.length === 0 && <tr><td className="px-3 py-3 text-muted">No payments yet.</td></tr>}
                    {revenue.months?.map((m) => (
                      <tr key={m.month} className="border-b border-line/50 last:border-0">
                        <td className="px-4 py-2 text-muted">{m.month}</td>
                        <td className="px-4 py-2 text-right">{inr(m.total_paise)}</td>
                      </tr>
                    ))}
                  </table>
                </div>
              </>
            )}
          </section>
        </div>

        {/* Translations (i18n drafts — correct the wording) */}
        <section className="mt-14" data-testid="admin-i18n-section">
          <h2 className="font-display text-2xl font-extrabold">Translations</h2>
          <p className="mt-1 text-sm text-muted">
            First drafts made with Claude for the website pages (home, pricing, contact, login, sign-up, billing).
            Correct the wording here — hand-checked wording is better. A row left empty falls back to English.
            The bot page keeps its own translations.
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <select value={i18nLang} onChange={(e) => loadI18n(e.target.value)} data-testid="admin-i18n-lang"
              className="rounded-full border border-line bg-surface px-4 py-2 text-sm outline-none focus:border-accent">
              {I18N_LANGS.map(([code, label]) => <option key={code} value={code}>{label}</option>)}
            </select>
            <button onClick={saveI18n} data-testid="admin-i18n-save"
              className="rounded-full bg-accent px-6 py-2 text-sm font-bold text-white transition-transform hover:-translate-y-0.5">
              Save translations
            </button>
            <span className="font-mono text-xs text-muted">{i18nKeys.length} rows</span>
          </div>
          <div className="mt-4 max-h-[480px] overflow-y-auto rounded-2xl border border-line bg-surface">
            <table className="w-full text-left text-sm" data-testid="admin-i18n-table">
              <thead className="sticky top-0 bg-surface">
                <tr className="border-b border-line font-mono text-xs uppercase tracking-wider text-muted">
                  <th className="px-4 py-3 w-1/2">English</th>
                  <th className="px-4 py-3">{I18N_LANGS.find((l) => l[0] === i18nLang)?.[1] || i18nLang}</th>
                </tr>
              </thead>
              <tbody>
                {i18nKeys.length === 0 && (
                  <tr><td colSpan={2} className="px-4 py-6 text-center text-muted">No rows yet for this language.</td></tr>
                )}
                {i18nKeys.map((k, i) => (
                  <tr key={k} className="border-b border-line/60 last:border-0">
                    <td className="px-4 py-2 align-top text-muted">{k}</td>
                    <td className="px-2 py-1.5">
                      <input
                        value={i18nStrings[k] || ""}
                        onChange={(e) => setI18nStrings((s) => ({ ...s, [k]: e.target.value }))}
                        data-testid={`admin-i18n-row-${i}`}
                        className="w-full rounded-lg border border-transparent bg-transparent px-2 py-1.5 text-sm outline-none hover:border-line focus:border-accent focus:bg-bg"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Dubbing */}
        <section className="mt-14 rounded-3xl border border-line bg-surface p-7 shadow-card" data-testid="admin-dubbing-section">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="font-display text-2xl font-extrabold">Dubbing</h2>
            {dub && (
              <span className={`rounded-full border px-4 py-1 text-xs font-bold ${dub.provider_configured ? "border-good text-good" : "border-warn text-warn"}`} data-testid="admin-dubbing-provider">
                {dub.provider} · {dub.provider_configured ? "key configured" : "key missing (ELEVENLABS_API_KEY in Secrets)"}
              </span>
            )}
          </div>
          {dub && (
            <div className="mt-4 grid grid-cols-2 gap-3 text-center sm:grid-cols-4">
              <div className="rounded-2xl bg-bg p-4">
                <p className="font-mono text-xs text-muted">Jobs today</p>
                <p className="mt-1 font-display text-2xl font-extrabold" data-testid="dub-jobs-today">{dub.jobs_today}</p>
              </div>
              <div className="rounded-2xl bg-bg p-4">
                <p className="font-mono text-xs text-muted">Failures today</p>
                <p className="mt-1 font-display text-2xl font-extrabold" data-testid="dub-failures-today">{dub.failures_today}</p>
              </div>
              <div className="rounded-2xl bg-bg p-4">
                <p className="font-mono text-xs text-muted">Charged today</p>
                <p className="mt-1 font-display text-2xl font-extrabold">{inr(dub.charged_today)}</p>
              </div>
              <div className="rounded-2xl bg-bg p-4">
                <p className="font-mono text-xs text-muted">Refunded today</p>
                <p className="mt-1 font-display text-2xl font-extrabold">{inr(dub.refunded_today)}</p>
              </div>
            </div>
          )}
          {settings && (
            <>
              <div className="mt-5 grid gap-4 sm:grid-cols-2">
                <label className="flex items-center gap-3 rounded-xl border border-line px-4 py-3 text-sm" data-testid="dubbing-enabled-wrap">
                  <input type="checkbox" checked={settings.dubbing_enabled ?? true} onChange={(e) => set("dubbing_enabled", e.target.checked)} data-testid="admin-setting-dubbing-enabled" className="h-4 w-4" />
                  Dubbing switched on (uncheck to disable instantly)
                </label>
                {input("dub_daily_cap", "Daily spend cap per user (₹→paise, e.g. 200000)", "number", "admin-setting-dub-cap")}
                {input("upload_max_mb", "Upload max size (MB)", "number", "admin-setting-upload-mb")}
                {input("upload_max_sec", "Upload max length (seconds)", "number", "admin-setting-upload-sec")}
                {input("dub_retention_days", "Keep files (days)", "number", "admin-setting-dub-retention")}
                {input("dub_cost_per_min_inr", "Provider cost ₹/min (before markup)", "number", "admin-setting-dub-cost")}
              </div>
              <p className="mt-3 text-xs text-muted">Save with the Save settings button above.</p>
            </>
          )}
        </section>

        {/* AI usage */}
        <section className="mt-14" data-testid="admin-usage-section">
          <h2 className="font-display text-2xl font-extrabold">AI usage</h2>
          <div className="mt-4 overflow-x-auto rounded-2xl border border-line bg-surface">
            <table className="w-full text-left text-sm" data-testid="admin-usage-table">
              <thead>
                <tr className="border-b border-line font-mono text-xs uppercase tracking-wider text-muted">
                  <th className="px-4 py-3">User</th>
                  <th className="px-4 py-3 text-right">Requests</th>
                  <th className="px-4 py-3 text-right">Est. tokens</th>
                  <th className="px-4 py-3">Last used</th>
                </tr>
              </thead>
              <tbody className="font-mono text-sm">
                {usage.length === 0 && <tr><td colSpan={4} className="px-4 py-6 text-center text-muted">No AI usage yet.</td></tr>}
                {usage.map((u) => (
                  <tr key={u.user_id} className="border-b border-line/60 last:border-0">
                    <td className="px-4 py-3 font-sans">{u.name} <span className="text-xs text-muted">({u.email})</span></td>
                    <td className="px-4 py-3 text-right">{u.requests}</td>
                    <td className="px-4 py-3 text-right">{u.est_tokens}</td>
                    <td className="px-4 py-3 text-xs text-muted">{(u.last_used || "").slice(0, 19).replace("T", " ")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </main>

      {adjust && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-ink/50 p-4 backdrop-blur-sm" data-testid="wallet-adjust-modal">
          <div className="w-full max-w-sm rounded-3xl border border-line bg-surface p-7 shadow-lift">
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-muted">Adjust wallet</p>
            <h3 className="mt-2 font-display text-xl font-extrabold">{adjust.name}</h3>
            <p className="mt-1 text-xs text-muted">Use a negative amount (e.g. −100) to debit.</p>
            <div className="mt-4 space-y-3">
              <input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Amount in ₹" data-testid="wallet-adjust-amount"
                className="w-full rounded-xl border border-line px-3 py-2 text-sm outline-none focus:border-accent" />
              <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason" data-testid="wallet-adjust-reason"
                className="w-full rounded-xl border border-line px-3 py-2 text-sm outline-none focus:border-accent" />
              <div className="flex gap-2 pt-1">
                <button onClick={() => setAdjust(null)} data-testid="wallet-adjust-cancel" className="flex-1 rounded-full border border-line px-4 py-2.5 text-sm font-bold hover:border-accent">Cancel</button>
                <button onClick={doAdjust} disabled={!amount || !reason} data-testid="wallet-adjust-save"
                  className="flex-1 rounded-full bg-accent px-4 py-2.5 text-sm font-bold text-white disabled:opacity-50">Adjust</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
